import { FC, useEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import { isNaNOrUndefined } from 'src/common/utils';
import i18next, { Languages } from '../../../i18n';
import * as React from 'react';

export interface NumberInputProps {
  onChange?(value: number | undefined): void;
  value?: number;
  unitsSymbol?: string;
  placeholder?: string;
  decimalSeparator?: DecimalSeparator;
  regexp?: RegExp;
  disabled?: boolean;
  acceptUndefined?: boolean;
  onlyIntegers?: boolean;
  readOnly?: boolean;
}
export enum DecimalSeparator {
  Dot = 'Dot',
  Comma = 'Comma',
}

export const getDecimalSeparator = (separator: DecimalSeparator) => {
  switch (separator) {
    case DecimalSeparator.Dot:
      return '.';
    case DecimalSeparator.Comma:
      return ',';
    default:
      return '';
  }
};
export const getDefaultDecimalSeparator = () => {
  var l = i18next.language === Languages.English ? DecimalSeparator.Dot : DecimalSeparator.Comma;
  return l;
};
export const getNumberRegexp = (separator: DecimalSeparator) => {
  switch (separator) {
    case DecimalSeparator.Dot:
      return /^([-]?((\d+)((.)\d*)?)?)?$/;
    case DecimalSeparator.Comma:
      return /^([-]?((\d+)((,)\d*)?)?)?$/;
    default:
      return /^([-]?((\d+)((.)\d*)?)?)?$/;
  }
};
export const convertToDotSeparator = (value: string, convertFromSeparator: DecimalSeparator) => {
  return value.replace(getDecimalSeparator(convertFromSeparator), getDecimalSeparator(DecimalSeparator.Dot));
};
export const convertFromDotSeparator = (value: string, convertToSeparator: DecimalSeparator) => {
  return value.replace(getDecimalSeparator(DecimalSeparator.Dot), getDecimalSeparator(convertToSeparator));
};
const NumberInputComponent: FC<NumberInputProps> = ({
  onChange = () => {},
  value,
  unitsSymbol,
  placeholder,
  decimalSeparator,
  regexp,
  disabled,
  acceptUndefined = false,
  onlyIntegers = false,
  readOnly = false,
}) => {
  const inputRef = useRef();
  const separator = decimalSeparator || getDefaultDecimalSeparator();
  const regularExpression = regexp || getNumberRegexp(separator);
  const [previousValue, setPreviousValue] = useState<string>('');
  const [stringValue, setStringValue] = useState<string>('');
  const getInitialValue = () => {
    if (isNaNOrUndefined(value)) {
      return '';
    }

    let stringValue = value!.toLocaleString('fullwide', { useGrouping: false, maximumSignificantDigits: 21 });
    stringValue = stringValue.replace(getDecimalSeparator(DecimalSeparator.Dot), getDecimalSeparator(separator));
    stringValue = stringValue.replace(getDecimalSeparator(DecimalSeparator.Comma), getDecimalSeparator(separator));
    return stringValue;
  };
  useEffect(() => {
    var stringValue = getInitialValue();
    setStringValue(stringValue);
    setPreviousValue(stringValue);
  }, [inputRef, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const onValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!regularExpression.test(event.target.value)) {
      setStringValue(previousValue);
      return;
    }

    var valueWithDotSeparator: string = event.target.value;
    if (valueWithDotSeparator === '' && acceptUndefined) {
      setStringValue('');
      setPreviousValue('');
      onChange(undefined);
      return;
    }

    if (onlyIntegers) {
      let integer = parseInt(event.target.value);
      if (Number.isNaN(integer)) {
        setStringValue('');
        setPreviousValue('');
        onChange(undefined);
        return;
      }

      setPreviousValue(`${integer}`);
      setStringValue(`${integer}`);
      onChange(integer);
      return;
    }

    if (separator !== DecimalSeparator.Dot) {
      valueWithDotSeparator = convertToDotSeparator(event.target.value, separator);
    }
    setPreviousValue(event.target.value);
    setStringValue(event.target.value);
    onChange(+valueWithDotSeparator);
  };
  return (
    <>
      <Input
        className={readOnly ? 'numeric-input-normal-looking-disabled' : undefined}
        readOnly={readOnly}
        placeholder={placeholder || ''}
        addonAfter={unitsSymbol}
        autoComplete="off"
        value={stringValue}
        onChange={onValueChange}
        disabled={disabled || readOnly}
      />
    </>
  );
};

export default NumberInputComponent;
